import { clamp, clamp01, smoothstep } from '../core/math';

/**
 * Pure engine-sound model (no Web Audio, unit-tested in node). `engineVoice.ts` turns the numbers into
 * a node graph, `engine.ts` runs the voice pool.
 *
 * How the engine sounds (see src/audio/README.md):
 * - Timbre: one engine *cycle* (two crank turns, every cylinder fires once) is synthesised as a
 *   firing-order pulse train — a pressure pulse per cylinder with exhaust ring, per-cylinder
 *   irregularity (cross-plane V8 burble), diesel knock — and turned into a band-limited PeriodicWave
 *   whose fundamental is rpm/120. Band-limited by construction, so no aliasing; no waveshaper.
 *   An on-throttle wave (sharp pulses, growl) and an off-throttle wave (round, lumpy) are crossfaded
 *   by the smoothed throttle.
 * - Pitch: the audio layer runs its own gentle gearbox from road speed (a realistic ~35 % rpm drop on
 *   an upshift instead of the telemetry's fall to the bottom of the rev range), slew-limited, with a
 *   slow wander so a steady cruise is never a static whine.
 * - Gear change: a smooth clutch dip (throttle off, gain and brightness down for ~0.3 s).
 * - Off-throttle at high rpm: an overrun burble with the odd pop for the characterful cars.
 */

export type EngineLayout = 'even' | 'crossplane' | 'diesel';

export interface EngineProfile {
  id: string;
  /** Cylinders (4-stroke: firing frequency = rpm / 60 * cylinders / 2). */
  cylinders: number;
  idleRpm: number;
  redlineRpm: number;
  /** Firing pattern: even inline/flat engine, cross-plane V8 (uneven per-bank pulses), diesel (hard knock). */
  layout: EngineLayout;
  /** Per-cylinder pulse irregularity 0..1 (lumpy idle, burble). */
  irregular: number;
  /** On-throttle pulse sharpness 0..1: more = brighter, more snarl. */
  snarl: number;
  /** Exhaust ring after each pulse: frequency (in firing harmonics) and strength. */
  ringHarm: number;
  ring: number;
  /** Diesel combustion clatter 0..1. */
  clatter: number;
  /** Pure sine at the firing frequency, for weight (relative to the pulse wave). */
  sub: number;
  /** Exhaust body resonance: peaking filter centre (Hz) and boost (dB). */
  bodyHz: number;
  bodyDb: number;
  /** Intake roar: noise band centre at idle (Hz) and level. */
  intakeHz: number;
  intake: number;
  /** Lowpass cutoff (Hz) = cutBase + cutRpm * rpmNorm + cutLoad * throttle. */
  cutBase: number;
  cutRpm: number;
  cutLoad: number;
  /** Voice gain = gainIdle + gainRpm * rpmNorm + gainLoad * throttle (before distance and bus). */
  gainIdle: number;
  gainRpm: number;
  gainLoad: number;
  /** Overrun burble / pops when lifting off at high rpm (0..1). */
  overrun: number;
  /** Idle lope (slow gain wobble) 0..1. */
  lope: number;
}

const P = (p: EngineProfile): EngineProfile => p;

/** Character profiles keyed by vehicle spec id. rpm ranges match `VEHICLE_SPECS`. */
export const ENGINE_PROFILES: Record<string, EngineProfile> = {
  // Small saloon putter: even little four, round and modest, a hint of rasp when wrung out.
  compact: P({ id: 'compact', cylinders: 4, idleRpm: 900, redlineRpm: 6800, layout: 'even', irregular: 0.18, snarl: 0.35, ringHarm: 2.6, ring: 0.22, clatter: 0, sub: 0.18, bodyHz: 230, bodyDb: 4, intakeHz: 650, intake: 0.05, cutBase: 520, cutRpm: 1500, cutLoad: 800, gainIdle: 0.147, gainRpm: 0.098, gainLoad: 0.118, overrun: 0.12, lope: 0.12 }),
  // Big cross-plane V8: lumpy idle, deep burble, warm growl.
  saloon: P({ id: 'saloon', cylinders: 8, idleRpm: 750, redlineRpm: 5200, layout: 'crossplane', irregular: 0.6, snarl: 0.42, ringHarm: 2.2, ring: 0.3, clatter: 0, sub: 0.32, bodyHz: 135, bodyDb: 5, intakeHz: 480, intake: 0.04, cutBase: 380, cutRpm: 1200, cutLoad: 900, gainIdle: 0.157, gainRpm: 0.079, gainLoad: 0.114, overrun: 0.45, lope: 0.4 }),
  // Diesel-ish van: clattery, flat, a little gruff.
  van: P({ id: 'van', cylinders: 4, idleRpm: 700, redlineRpm: 4500, layout: 'diesel', irregular: 0.25, snarl: 0.25, ringHarm: 3.2, ring: 0.15, clatter: 0.55, sub: 0.3, bodyHz: 170, bodyDb: 3, intakeHz: 950, intake: 0.05, cutBase: 460, cutRpm: 1100, cutLoad: 700, gainIdle: 0.15, gainRpm: 0.078, gainLoad: 0.097, overrun: 0.04, lope: 0.22 }),
  // Sports coupe straight-six: smooth, eager snarl, crackles on the overrun.
  coupe: P({ id: 'coupe', cylinders: 6, idleRpm: 1000, redlineRpm: 7800, layout: 'even', irregular: 0.2, snarl: 0.8, ringHarm: 3.6, ring: 0.42, clatter: 0, sub: 0.14, bodyHz: 310, bodyDb: 5, intakeHz: 850, intake: 0.07, cutBase: 650, cutRpm: 1700, cutLoad: 1100, gainIdle: 0.151, gainRpm: 0.108, gainLoad: 0.14, overrun: 0.65, lope: 0.08 }),
  // Patrol V8: like the saloon but tighter and a touch harder.
  police: P({ id: 'police', cylinders: 8, idleRpm: 800, redlineRpm: 5800, layout: 'crossplane', irregular: 0.42, snarl: 0.6, ringHarm: 2.6, ring: 0.34, clatter: 0, sub: 0.28, bodyHz: 165, bodyDb: 4.5, intakeHz: 600, intake: 0.05, cutBase: 440, cutRpm: 1600, cutLoad: 1050, gainIdle: 0.16, gainRpm: 0.094, gainLoad: 0.122, overrun: 0.35, lope: 0.28 }),
  // Diesel taxi: the chattering one.
  cab: P({ id: 'cab', cylinders: 4, idleRpm: 700, redlineRpm: 4400, layout: 'diesel', irregular: 0.28, snarl: 0.2, ringHarm: 3.4, ring: 0.12, clatter: 0.7, sub: 0.26, bodyHz: 190, bodyDb: 3, intakeHz: 1000, intake: 0.045, cutBase: 430, cutRpm: 1000, cutLoad: 650, gainIdle: 0.15, gainRpm: 0.07, gainLoad: 0.088, overrun: 0.03, lope: 0.25 }),
  // The police van (Haulmaster in police trim): the van's diesel clatter, pushed harder and a little rougher.
  policevan: P({ id: 'policevan', cylinders: 4, idleRpm: 700, redlineRpm: 4500, layout: 'diesel', irregular: 0.28, snarl: 0.32, ringHarm: 3.0, ring: 0.18, clatter: 0.5, sub: 0.32, bodyHz: 160, bodyDb: 3.5, intakeHz: 900, intake: 0.055, cutBase: 470, cutRpm: 1150, cutLoad: 760, gainIdle: 0.15, gainRpm: 0.08, gainLoad: 0.1, overrun: 0.05, lope: 0.22 }),
  // Lorry: slow, heavy six-cylinder diesel, mostly felt.
  // Estate straight-six: smooth and unhurried, a family-car hum.
  estate: P({ id: 'estate', cylinders: 6, idleRpm: 750, redlineRpm: 5000, layout: 'even', irregular: 0.22, snarl: 0.3, ringHarm: 2.4, ring: 0.22, clatter: 0, sub: 0.24, bodyHz: 160, bodyDb: 4, intakeHz: 520, intake: 0.04, cutBase: 400, cutRpm: 1200, cutLoad: 800, gainIdle: 0.152, gainRpm: 0.082, gainLoad: 0.11, overrun: 0.2, lope: 0.18 }),
  // The Grandee's big V8: deep, soft-spoken at idle, a long rising roar.
  exec: P({ id: 'exec', cylinders: 8, idleRpm: 650, redlineRpm: 5000, layout: 'crossplane', irregular: 0.5, snarl: 0.5, ringHarm: 2.0, ring: 0.3, clatter: 0, sub: 0.4, bodyHz: 118, bodyDb: 5.5, intakeHz: 430, intake: 0.05, cutBase: 340, cutRpm: 1300, cutLoad: 1000, gainIdle: 0.155, gainRpm: 0.084, gainLoad: 0.124, overrun: 0.5, lope: 0.34 }),
  // The Linnet's rorty little four: buzzy, revvy, pops on the overrun.
  roadster: P({ id: 'roadster', cylinders: 4, idleRpm: 950, redlineRpm: 7200, layout: 'even', irregular: 0.2, snarl: 0.7, ringHarm: 3.2, ring: 0.36, clatter: 0, sub: 0.12, bodyHz: 280, bodyDb: 4.5, intakeHz: 820, intake: 0.07, cutBase: 600, cutRpm: 1700, cutLoad: 1000, gainIdle: 0.149, gainRpm: 0.104, gainLoad: 0.132, overrun: 0.6, lope: 0.1 }),
  lorry: P({ id: 'lorry', cylinders: 6, idleRpm: 600, redlineRpm: 3200, layout: 'diesel', irregular: 0.32, snarl: 0.3, ringHarm: 2.4, ring: 0.2, clatter: 0.4, sub: 0.5, bodyHz: 105, bodyDb: 5, intakeHz: 420, intake: 0.05, cutBase: 300, cutRpm: 700, cutLoad: 550, gainIdle: 0.116, gainRpm: 0.052, gainLoad: 0.069, overrun: 0.05, lope: 0.4 }),
};

/** Fallback for specs added later (traffic/police/world may extend VEHICLE_SPECS). */
export const GENERIC_PROFILE: EngineProfile = { ...(ENGINE_PROFILES.compact as EngineProfile), id: 'generic', idleRpm: 800, redlineRpm: 6000 };

export function profileFor(specId: string): EngineProfile {
  return ENGINE_PROFILES[specId] ?? GENERIC_PROFILE;
}

/** Firing frequency of a 4-stroke engine. */
export function firingFrequency(rpm: number, cylinders: number): number {
  return (Math.max(0, rpm) / 60) * (cylinders / 2);
}

/** Engine-cycle frequency (two crank turns): the PeriodicWave fundamental. */
export function cycleFrequency(rpm: number): number {
  return Math.max(0, rpm) / 120;
}

// ---------------------------------------------------------------------------------------------
// Cycle waveform (firing-order pulse train) -> Fourier series for a PeriodicWave.

export type WaveMode = 'on' | 'off';

export interface CycleWave {
  real: Float32Array<ArrayBuffer>;
  imag: Float32Array<ArrayBuffer>;
}

/** Cross-plane V8 bank sequence (which exhaust each firing goes down): the source of the burble. */
const CROSSPLANE_BANKS = [0, 1, 0, 0, 1, 0, 1, 1];

function seeded(id: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  let s = h >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/** Samples of one engine cycle (exported for tests). */
export function cycleSamples(p: EngineProfile, mode: WaveMode, n = 2048): Float64Array {
  const rand = seeded(`${p.id}:${p.cylinders}`);
  const cyl = Math.max(1, Math.round(p.cylinders));
  const buf = new Float64Array(n);
  const slot = n / cyl;
  const on = mode === 'on';
  // Pulse rise time as a fraction of the firing interval: sharp on throttle, round on the overrun.
  const rise = on ? 0.03 + 0.08 * (1 - clamp01(p.snarl)) : 0.16 + 0.06 * (1 - clamp01(p.snarl));
  const ringAmt = p.ring * (on ? 1 : 0.35);
  const clat = p.clatter * (on ? 1 : 0.55);
  const irr = clamp01(p.irregular);
  for (let i = 0; i < cyl; i++) {
    const jitter = (rand() - 0.5) * (p.layout === 'crossplane' ? 0.06 : p.layout === 'diesel' ? 0.02 : 0.035) * irr;
    let amp = 1 + (rand() - 0.5) * 0.5 * irr;
    if (p.layout === 'crossplane') amp += (CROSSPLANE_BANKS[i % 8] ? 1 : -1) * 0.28 * irr;
    // Off throttle: weak, uneven combustion (the lumpy overrun burble).
    if (!on) amp *= 0.5 + 0.5 * (1 - rand() * (0.4 + 0.6 * irr));
    const start = (i + jitter) * slot;
    const len = Math.floor(slot * 1.8);
    for (let u = 0; u < len; u++) {
      const tau = u / slot;
      const main = (tau / rise) * Math.exp(1 - tau / rise);
      const ring = ringAmt * Math.exp(-tau / 0.3) * Math.sin(2 * Math.PI * p.ringHarm * tau);
      const knock = clat * Math.exp(-tau / 0.04) * Math.sin(2 * Math.PI * 10 * tau) * 2.4;
      const idx = (((Math.floor(start) + u) % n) + n) % n;
      buf[idx] = (buf[idx] as number) + amp * (main + ring + knock);
    }
  }
  let mean = 0;
  for (let i = 0; i < n; i++) mean += buf[i] as number;
  mean /= n;
  for (let i = 0; i < n; i++) buf[i] = (buf[i] as number) - mean;
  return buf;
}

/** Number of cycle harmonics kept: 24 firing harmonics. */
export function waveHarmonics(p: EngineProfile): number {
  return Math.min(400, Math.max(32, Math.round(p.cylinders * 24)));
}

const dftTables = new Map<number, { cos: Float64Array; sin: Float64Array }>();

/** cos / sin of 2πj/n for j < n (built once per n). */
function dftTable(n: number): { cos: Float64Array; sin: Float64Array } {
  let t = dftTables.get(n);
  if (!t) {
    t = { cos: new Float64Array(n), sin: new Float64Array(n) };
    for (let j = 0; j < n; j++) {
      t.cos[j] = Math.cos((2 * Math.PI * j) / n);
      t.sin[j] = Math.sin((2 * Math.PI * j) / n);
    }
    dftTables.set(n, t);
  }
  return t;
}

/**
 * Fourier coefficients of one engine cycle, with a gentle spectral tilt (the off-throttle wave is
 * darker). Index k = k-th harmonic of the cycle frequency; [0] is DC (always 0).
 */
export function buildCycleWave(p: EngineProfile, mode: WaveMode): CycleWave {
  const n = 2048;
  const x = cycleSamples(p, mode, n);
  const H = waveHarmonics(p);
  const real = new Float32Array(H + 1);
  const imag = new Float32Array(H + 1);
  const cyl = Math.max(1, p.cylinders);
  const kc = (mode === 'on' ? 2.25 + 5 * clamp01(p.snarl) : 1.75 + clamp01(p.snarl)) + 4.5 * clamp01(p.clatter);
  // cos / sin of 2πki/n read from one table at (k·i mod n): the same sums without ~1.5 M trig calls, which
  // made the first car of each profile heard a 20–60 ms stall in the audio update (docs/PERF.md).
  const { cos, sin } = dftTable(n);
  for (let k = 1; k <= H; k++) {
    let re = 0;
    let im = 0;
    let j = 0;
    for (let i = 0; i < n; i++) {
      const v = x[i] as number;
      re += v * (cos[j] as number);
      im -= v * (sin[j] as number);
      j += k;
      if (j >= n) j -= n;
    }
    const kf = k / cyl;
    const tilt = 1 / Math.sqrt(1 + Math.pow(kf / kc, 4));
    real[k] = ((2 * re) / n) * tilt;
    imag[k] = ((-2 * im) / n) * tilt;
  }
  // Normalise to a fixed RMS (not peak) so the on/off crossfade and the sub keep their intended balance.
  let e = 0;
  for (let k = 1; k <= H; k++) e += ((real[k] as number) ** 2 + (imag[k] as number) ** 2) / 2;
  const norm = e > 0 ? WAVE_RMS / Math.sqrt(e) : 0;
  for (let k = 1; k <= H; k++) {
    real[k] = (real[k] as number) * norm;
    imag[k] = (imag[k] as number) * norm;
  }
  return { real, imag };
}

/** RMS of every cycle wave (the graph creates them with normalisation disabled). */
export const WAVE_RMS = 0.3;

// ---------------------------------------------------------------------------------------------
// Drive state: smoothing, gearbox, shift envelope, overrun.

/** Telemetry subset the engine sound needs. */
export interface EngineInput {
  /** Telemetry rpm (used when standing still: revving in neutral). */
  rpm: number;
  /** -1..1 applied throttle (negative = braking, or accelerating in reverse). */
  throttle: number;
  reversing: boolean;
  /** Signed forward speed (m/s). */
  forwardSpeed: number;
  /** Spec top speed and gear count. */
  maxSpeed: number;
  gears: number;
  /** 0..1 wheelspin. */
  wheelspin: number;
}

export interface EngineDrive {
  /** Smoothed audio rpm. */
  rpm: number;
  /** Smoothed on-throttle amount 0..1. */
  on: number;
  gear: number;
  /** Seconds since the last upshift (large = none recent). */
  sinceShift: number;
  /** Seconds since the throttle was lifted (0 while on throttle). */
  offFor: number;
  /** Slow rpm wander (fraction). */
  wander: number;
  wanderV: number;
  init: boolean;
}

export function createEngineDrive(): EngineDrive {
  return { rpm: 0, on: 0, gear: 1, sinceShift: 99, offFor: 99, wander: 0, wanderV: 0, init: false };
}

/** Gear-change dip duration (s). */
export const SHIFT_TIME = 0.3;
/** Normalised rpm right after an upshift and at the shift point (auto box, full throttle). */
export const SHIFT_LOW = 0.55;
export const SHIFT_HIGH = 0.9;

/** Gear (1..gears) and position within it (0..1) for a speed fraction of top speed. */
export function gearFor(speedFrac: number, gears: number): { gear: number; within: number } {
  const g = Math.max(1, Math.round(gears));
  const f = clamp01(speedFrac);
  const gear = Math.min(g, Math.floor(f * g) + 1);
  return { gear, within: clamp01(f * g - (gear - 1)) };
}

/**
 * Normalised rpm (0 = idle, 1 = redline) from the audio gearbox. First gear pulls from just over idle;
 * later gears start at SHIFT_LOW (a ~35 % drop, like real ratios). Light throttle shifts earlier.
 */
export function gearboxRpmNorm(gear: number, within: number, gears: number, on: number): number {
  const top = gear >= gears ? 1 : SHIFT_HIGH;
  const lo = gear <= 1 ? 0.1 : SHIFT_LOW;
  const raw = lo + (top - lo) * clamp01(within);
  return raw * (0.72 + 0.28 * clamp01(on));
}

/** Clutch dip 0..1 at `t` seconds after an upshift: quick smooth fall, short hold, smooth recovery. */
export function shiftEnvelope(t: number): number {
  if (!(t >= 0) || t >= SHIFT_TIME) return 0;
  if (t < 0.05) return smoothstep(0, 1, t / 0.05);
  if (t < 0.09) return 1;
  return 1 - smoothstep(0, 1, (t - 0.09) / (SHIFT_TIME - 0.09));
}

/** Max pitch change rates (semitones per second): a full-range rev takes ~1 s, an upshift drop ~0.15 s. */
export const RPM_RISE_ST = 38;
export const RPM_FALL_ST = 50;

/**
 * Advance the drive state one frame. `rand` drives the cruise wander (Math.random in game,
 * seeded in tests).
 */
export function stepEngineDrive(s: EngineDrive, p: EngineProfile, inp: EngineInput, dt: number, rand: () => number = Math.random): EngineDrive {
  const d = Math.max(0, Math.min(0.1, Number.isFinite(dt) ? dt : 0));
  const span = Math.max(1, p.redlineRpm - p.idleRpm);
  const thr = Number.isFinite(inp.throttle) ? inp.throttle : 0;
  // Braking is negative throttle: that is *off* throttle, not load (the old engine roared under braking).
  const onTarget = clamp01(inp.reversing ? -thr : thr);
  const speed = Math.abs(Number.isFinite(inp.forwardSpeed) ? inp.forwardSpeed : 0);
  const frac = inp.maxSpeed > 0 ? speed / inp.maxSpeed : 0;
  const gb = gearFor(frac, inp.gears);

  if (!s.init) {
    s.init = true;
    s.on = onTarget;
    s.gear = gb.gear;
    s.rpm = p.idleRpm;
  }
  // Throttle follower: a quick but never instant attack, a slightly slower release.
  const tc = onTarget > s.on ? 0.09 : 0.13;
  s.on += (onTarget - s.on) * (1 - Math.exp(-d / tc));

  if (gb.gear > s.gear && !inp.reversing && speed > 1) s.sinceShift = 0;
  else s.sinceShift += d;
  s.gear = gb.gear;
  if (onTarget < 0.08) s.offFor += d;
  else s.offFor = 0;

  let targetNorm: number;
  if (speed < 0.6 || inp.reversing) {
    // Standing / reversing: follow the telemetry (revving in neutral, reverse gear).
    targetNorm = clamp01(((Number.isFinite(inp.rpm) ? inp.rpm : p.idleRpm) - p.idleRpm) / span);
    if (inp.reversing) targetNorm = Math.max(targetNorm * 0.8, 0.1 + 0.5 * clamp01(speed / 9));
  } else {
    targetNorm = gearboxRpmNorm(gb.gear, gb.within, inp.gears, s.on);
  }
  targetNorm = clamp01(targetNorm + 0.25 * (Number.isFinite(inp.wheelspin) ? clamp01(inp.wheelspin) : 0) * s.on);
  const target = p.idleRpm + span * targetNorm;
  // One-pole smoothing, then a slew limit so no gear change or lift-off is an audible pitch jump.
  let next = s.rpm + (target - s.rpm) * (1 - Math.exp(-d / 0.05));
  next = clamp(next, s.rpm * Math.pow(2, (-RPM_FALL_ST * d) / 12), s.rpm * Math.pow(2, (RPM_RISE_ST * d) / 12));
  s.rpm = clamp(next, p.idleRpm * 0.9, p.redlineRpm * 1.02);

  // Ornstein-Uhlenbeck wander: about +-0.6 % rpm, a few seconds' correlation. Never a static drone.
  s.wanderV += (-s.wanderV * 3 - s.wander * 4 + (rand() - 0.5) * 0.9) * d;
  s.wander = clamp(s.wander + s.wanderV * d, -0.012, 0.012);
  return s;
}

/** Pops per second on the overrun (lifted throttle, high rpm, shortly after lifting). */
export function overrunRate(p: EngineProfile, rpmNorm: number, on: number, offFor: number, speed: number): number {
  if (on > 0.1 || speed < 4 || offFor > 2.2) return 0;
  const r = clamp01((rpmNorm - 0.3) / 0.55);
  return p.overrun * 5 * r * (1 - offFor / 2.2);
}

// ---------------------------------------------------------------------------------------------
// Parameters for the voice graph.

/** Hard ceiling on one engine voice's gain (the loudest profile at redline, full throttle, is ~0.4). */
export const MAX_ENGINE_GAIN = 0.42;

export interface EngineParams {
  /** Engine-cycle frequency (Hz): the PeriodicWave fundamental. */
  cycleHz: number;
  /** Firing frequency (Hz): the perceived pitch. */
  freq: number;
  rpmNorm: number;
  /** Effective throttle after the clutch dip. */
  on: number;
  /** Crossfade between the on-/off-throttle waves. */
  onGain: number;
  offGain: number;
  subGain: number;
  cutoff: number;
  bodyHz: number;
  bodyDb: number;
  /** Overall voice gain before distance/bus (bounded by MAX_ENGINE_GAIN). */
  gain: number;
  intakeGain: number;
  intakeHz: number;
  lopeHz: number;
  lopeDepth: number;
}

export function createEngineParams(): EngineParams {
  return { cycleHz: 0, freq: 0, rpmNorm: 0, on: 0, onGain: 0, offGain: 0, subGain: 0, cutoff: 0, bodyHz: 0, bodyDb: 0, gain: 0, intakeGain: 0, intakeHz: 0, lopeHz: 0, lopeDepth: 0 };
}

/**
 * Allocation-free: writes the synthesis parameters for (rpm, throttle, clutch dip, wander) into `out`.
 * `on` is 0..1 smoothed throttle, `shift` is the 0..1 clutch dip.
 */
export function engineParamsInto(out: EngineParams, p: EngineProfile, rpm: number, on: number, shift: number, wander = 0): EngineParams {
  const span = Math.max(1, p.redlineRpm - p.idleRpm);
  const r = Number.isFinite(rpm) ? rpm : p.idleRpm;
  const rn = clamp01((r - p.idleRpm) / span);
  const dip = clamp01(Number.isFinite(shift) ? shift : 0);
  const o = clamp01(Number.isFinite(on) ? on : 0) * (1 - dip);
  const rr = Math.max(0, r * (1 + clamp(Number.isFinite(wander) ? wander : 0, -0.02, 0.02)));
  out.cycleHz = cycleFrequency(rr);
  out.freq = firingFrequency(rr, p.cylinders);
  out.rpmNorm = rn;
  out.on = o;
  out.onGain = o;
  out.offGain = 0.62 * (1 - o);
  out.subGain = p.sub * 0.45;
  out.cutoff = (p.cutBase + p.cutRpm * rn + p.cutLoad * o) * (1 - 0.3 * dip);
  out.bodyHz = p.bodyHz * (1 + 0.25 * rn);
  out.bodyDb = p.bodyDb;
  out.gain = Math.min(MAX_ENGINE_GAIN, (p.gainIdle + p.gainRpm * rn + p.gainLoad * o) * (1 - 0.3 * dip));
  out.intakeGain = p.intake * (0.45 + 0.55 * o) * (0.5 + 0.5 * rn);
  out.intakeHz = p.intakeHz * (1 + 1.1 * rn);
  out.lopeHz = Math.max(1, r / 140);
  out.lopeDepth = out.gain * p.lope * (1 - rn) * (1 - rn) * (1 - 0.7 * o) * 0.45;
  return out;
}

export function engineParams(p: EngineProfile, rpm: number, on: number, shift: number, wander = 0): EngineParams {
  return engineParamsInto(createEngineParams(), p, rpm, on, shift, wander);
}

/**
 * Per-voice glue: drive state + params + overrun pop timing. Pure (rand injectable) so the game,
 * the offline lab and the tests run the identical model.
 */
export class EngineDriver {
  readonly drive: EngineDrive = createEngineDrive();
  readonly params: EngineParams = createEngineParams();
  private popWait = 0;

  constructor(private readonly rand: () => number = Math.random) {}

  reset(): void {
    Object.assign(this.drive, createEngineDrive());
    this.popWait = 0;
  }

  /** Advance one frame; returns the params to apply. */
  step(p: EngineProfile, inp: EngineInput, dt: number): EngineParams {
    const s = stepEngineDrive(this.drive, p, inp, dt, this.rand);
    return engineParamsInto(this.params, p, s.rpm, s.on, shiftEnvelope(s.sinceShift), s.wander);
  }

  /** Strength (0..1) of an overrun pop to fire this frame, or 0. Call after `step`. */
  pop(p: EngineProfile, speed: number, dt: number): number {
    const rate = overrunRate(p, this.params.rpmNorm, this.drive.on, this.drive.offFor, speed);
    if (rate <= 0) {
      this.popWait = 0;
      return 0;
    }
    this.popWait -= dt;
    if (this.popWait > 0) return 0;
    // Irregular gaps: exponential-ish with a floor, so pops come in little clusters.
    this.popWait = 0.03 + (-Math.log(1 - this.rand() * 0.98) / rate) * 0.8;
    return 0.35 + 0.65 * this.rand();
  }
}

// ---------------------------------------------------------------------------------------------
// Tyres.

export interface TyreParams {
  /** Tonal squeal level and pitch (Hz). */
  squeal: number;
  squealHz: number;
  /** Low scrub/rumble of a sliding tyre. */
  scrub: number;
  /** Metal scrape level and band (Hz). */
  scrape: number;
  scrapeHz: number;
}

export function createTyreParams(): TyreParams {
  return { squeal: 0, squealHz: 800, scrub: 0, scrape: 0, scrapeHz: 700 };
}

/**
 * Squeal is a pitched howl (~0.7-1.4 kHz), not a hiss: rises with slide and speed. Wet roads squeal
 * less and hiss more (scrub).
 */
export function tyreParamsInto(out: TyreParams, skid: number, speed: number, scrape: number, wet = 0): TyreParams {
  const s = clamp01(((Number.isFinite(skid) ? skid : 0) - 0.12) / 0.88);
  const v = Math.max(0, Number.isFinite(speed) ? speed : 0);
  const w = clamp01(wet);
  out.squeal = Math.pow(s, 1.5) * 0.2 * clamp01(v / 3) * (1 - 0.55 * w);
  out.squealHz = 680 + 380 * s + Math.min(v, 30) * 8;
  out.scrub = s * clamp01(v / 12) * (0.14 + 0.12 * w);
  const sc = clamp01(Number.isFinite(scrape) ? scrape : 0);
  out.scrape = sc * 0.3;
  out.scrapeHz = 600 + sc * 450;
  return out;
}

// ---------------------------------------------------------------------------------------------
// Distance.

/** Air absorption for other cars' engines: closes faster than the generic curve (never below 350 Hz). */
export function engineDistanceCutoff(distance: number): number {
  return clamp(9000 / (1 + Math.max(0, distance) / 8), 350, 16000);
}

/** Exaggerated-but-gentle speed of sound for engine Doppler (the siren uses a stronger one). */
export const ENGINE_DOPPLER_C = 220;
